import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import { CenterBadgesPage } from "./CenterBadgesPage";

type View="AWARD"|"LEDGER"|"DELIVERY"|"MANAGEMENT";

type ManagementRequest={
  requestId:string;
  credentialId:string;
  action:"REVOKE"|"REISSUE";
  status:string;
  reason:string;
  displayName:string;
  credentialName:string;
  createdAt:string;
  decisionReason?:string|null;
  replacementIssuanceRequestId?:string|null;
};

type LedgerItem={
  credentialId:string;
  externalCredentialId:string;
  credentialName:string;
  credentialDescription?:string|null;
  imageUri?:string|null;
  status:string;
  issuedAt:string;
  expiresAt?:string|null;
  verificationUri?:string|null;
  learnerId:string;
  displayName:string;
  yearCodeAtIssue?:string|null;
  gradeLevelAtIssue?:string|null;
  classLabelAtIssue?:string|null;
  schoolId:string;
  schoolName:string;
  currentSchoolId?:string|null;
  currentSchoolName?:string|null;
  movedSchool:boolean;
  emailStatus?:string|null;
  destinationMasked?:string|null;
  emailAttemptCount?:number|null;
  claimStatus?:string|null;
  claimExpiresAt?:string|null;
  managementRequest?:{
    requestId:string;
    action:string;
    status:string;
    reason:string;
    createdAt:string;
  }|null;
};

type Ledger={
  canManage:boolean;
  counts:{
    total:number;
    active:number;
    revoked:number;
    deliveryWaiting:number;
    claimCompleted:number;
    managementPending:number;
  };
  items:LedgerItem[];
  managementRequests:ManagementRequest[];
};

function formatDate(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit"
  });
}

function credentialStatus(value:string){
  const labels:Record<string,string>={
    ACTIVE:"유효",
    REVOKED:"회수됨",
    EXPIRED:"만료"
  };
  return labels[value] || value;
}

function deliveryStatus(value?:string|null){
  const labels:Record<string,string>={
    WAITING_CONTACT:"이메일 등록 대기",
    PENDING:"발송 대기",
    PREPARED:"발송 준비",
    ACCEPTED:"메일 전달",
    RETRYABLE_FAILURE:"재시도 대기",
    PERMANENT_FAILURE:"발송 실패",
    CLAIMED:"수령 완료"
  };
  return value ? labels[value] || value : "발송정보 없음";
}

function claimStatus(value?:string|null){
  const labels:Record<string,string>={
    ACTIVE:"수령 링크 활성",
    CLAIMED:"수령 완료",
    REVOKED:"수령 링크 취소",
    EXPIRED:"수령 링크 만료"
  };
  return value ? labels[value] || value : "미수령";
}

function requestAction(value:string){
  return value==="REVOKE" ? "회수" : "재발급";
}

export function CenterBadgeERPPage({
  client,
  schoolId
}:{
  client:PlatformClient;
  schoolId:string;
}){
  const [view,setView]=useState<View>("AWARD");

  return (
    <section className="badge-erp-shell">
      <header className="badge-erp-header">
        <div>
          <p className="eyebrow">Digital Badge ERP</p>
          <h2>디지털배지</h2>
          <p>배지 부여부터 발급대장, 학생 수령, 회수·재발급까지 학교 원장으로 관리합니다.</p>
        </div>
        <div className="badge-erp-principle">
          <span>학생 이동 시</span>
          <strong>배지·Life Passport 유지</strong>
        </div>
      </header>

      <nav className="badge-erp-tabs">
        {([
          ["AWARD","배지 주기"],
          ["LEDGER","발급대장"],
          ["DELIVERY","수령상태"],
          ["MANAGEMENT","회수·재발급"]
        ] as Array<[View,string]>).map(([value,label])=>(
          <button
            key={value}
            className={view===value ? "active" : ""}
            onClick={()=>setView(value)}
          >
            {label}
          </button>
        ))}
      </nav>

      {view==="AWARD" ? (
        <CenterBadgesPage client={client} schoolId={schoolId} />
      ) : (
        <BadgeLedgerWorkspace client={client} schoolId={schoolId} view={view} />
      )}
    </section>
  );
}

function BadgeLedgerWorkspace({
  client,
  schoolId,
  view
}:{
  client:PlatformClient;
  schoolId:string;
  view:Exclude<View,"AWARD">;
}){
  const [data,setData]=useState<Ledger|null>(null);
  const [query,setQuery]=useState("");
  const [status,setStatus]=useState("ALL");
  const [selectedCredential,setSelectedCredential]=useState<LedgerItem|null>(null);
  const [action,setAction]=useState<"REVOKE"|"REISSUE">("REVOKE");
  const [reason,setReason]=useState("");
  const [decisionReason,setDecisionReason]=useState("");
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  const [refresh,setRefresh]=useState(0);

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("center_badge_ledger",{p_school_id:schoolId}).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      setData(data as Ledger);
    });
    return()=>{active=false;};
  },[client,schoolId,refresh]);

  const items=useMemo(()=>{
    if(!data)return [];
    const q=query.trim().toLowerCase();
    return data.items.filter((item)=>{
      if(status!=="ALL" && item.status!==status)return false;
      if(!q)return true;
      return [
        item.displayName,item.credentialName,item.yearCodeAtIssue,
        item.gradeLevelAtIssue,item.classLabelAtIssue,item.schoolName,
        item.currentSchoolName,item.externalCredentialId
      ].filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  },[data,query,status]);

  const deliveryItems=useMemo(()=>{
    if(!data)return [];
    const q=query.trim().toLowerCase();
    return [...data.items]
      .filter((item)=>!q || [
        item.displayName,item.credentialName,item.destinationMasked,
        deliveryStatus(item.emailStatus),claimStatus(item.claimStatus)
      ].filter(Boolean).join(" ").toLowerCase().includes(q))
      .sort((a,b)=>{
        const order=(item:LedgerItem)=>{
          if(item.emailStatus==="WAITING_CONTACT")return 0;
          if(["RETRYABLE_FAILURE","PERMANENT_FAILURE"].includes(item.emailStatus || ""))return 1;
          if(item.emailStatus==="CLAIMED")return 4;
          return 2;
        };
        return order(a)-order(b);
      });
  },[data,query]);

  async function run(
    key:string,
    fn:()=>PromiseLike<{data?:unknown;error?:{message:string}|null}>,
    success:string
  ){
    setBusy(key);
    setError("");
    setMessage("");
    try{
      const result=await fn();
      if(result.error)throw new Error(result.error.message);
      setMessage(success);
      setRefresh((value)=>value+1);
      return result.data;
    }catch(err){
      setError(err instanceof Error ? err.message : "처리 중 오류가 발생했습니다.");
      return null;
    }finally{
      setBusy("");
    }
  }

  async function submitRequest(){
    if(!selectedCredential || reason.trim().length<3)return;
    const result=await run(
      "request",
      ()=>client.rpc("center_request_badge_management",{
        p_school_id:schoolId,
        p_credential_id:selectedCredential.credentialId,
        p_action:action,
        p_reason:reason.trim()
      }),
      action==="REVOKE" ? "배지 회수 요청을 등록했습니다." : "배지 재발급 요청을 등록했습니다."
    );
    if(result){
      setSelectedCredential(null);
      setReason("");
    }
  }

  async function decide(item:ManagementRequest,decision:"APPROVE"|"REJECT"){
    await run(
      "decision-"+item.requestId,
      ()=>client.rpc("center_decide_badge_management",{
        p_school_id:schoolId,
        p_request_id:item.requestId,
        p_decision:decision,
        p_reason:decisionReason.trim() || null
      }),
      decision==="APPROVE" ? requestAction(item.action)+" 처리를 승인했습니다." : "요청을 반려했습니다."
    );
    setDecisionReason("");
  }

  if(!data)return <p className="muted">디지털배지 원장을 불러오는 중…</p>;

  return (
    <div className="badge-ledger-workspace">
      {error && <p className="error">{error}</p>}
      {message && <p className="teacher-success">{message}</p>}

      <div className="badge-ledger-metrics">
        <Metric label="누적 발급" value={data.counts.total} />
        <Metric label="유효 배지" value={data.counts.active} />
        <Metric label="회수" value={data.counts.revoked} />
        <Metric label="수령 대기" value={data.counts.deliveryWaiting} />
        <Metric label="학생 수령" value={data.counts.claimCompleted} />
        <Metric label="관리 요청" value={data.counts.managementPending} />
      </div>

      {view==="LEDGER" && (
        <>
          <div className="badge-ledger-toolbar">
            <input
              className="teacher-search"
              value={query}
              onChange={(e)=>setQuery(e.target.value)}
              placeholder="학생·배지·학년도 검색"
            />
            <select value={status} onChange={(e)=>setStatus(e.target.value)}>
              <option value="ALL">전체 상태</option>
              <option value="ACTIVE">유효</option>
              <option value="REVOKED">회수됨</option>
              <option value="EXPIRED">만료</option>
            </select>
          </div>

          <div className="badge-ledger-table">
            <div className="badge-ledger-row head">
              <span>학생</span><span>배지</span><span>발급 당시</span><span>현재 학교</span><span>상태</span><span>관리</span>
            </div>
            {items.map((item)=>(
              <div className="badge-ledger-row" key={item.credentialId}>
                <span>
                  <strong>{item.displayName}</strong>
                  <small>{formatDate(item.issuedAt)}</small>
                </span>
                <span>
                  <strong>{item.credentialName}</strong>
                  <small>{item.externalCredentialId}</small>
                </span>
                <span>
                  <strong>{item.yearCodeAtIssue || "-"}</strong>
                  <small>{item.gradeLevelAtIssue || "-"}학년 · {item.classLabelAtIssue || "-"}반</small>
                </span>
                <span>
                  <strong>{item.currentSchoolName || "재학정보 없음"}</strong>
                  {item.movedSchool && <small className="moved-school">전학 후에도 배지 유지</small>}
                </span>
                <span className={item.status==="ACTIVE" ? "status-pill good" : "status-pill"}>
                  {credentialStatus(item.status)}
                </span>
                <span className="badge-ledger-actions">
                  {item.verificationUri && <a href={item.verificationUri} target="_blank" rel="noreferrer">검증</a>}
                  {!item.managementRequest || item.managementRequest.status!=="REQUESTED" ? (
                    <>
                      {item.status==="ACTIVE" && (
                        <button onClick={()=>{setSelectedCredential(item);setAction("REVOKE");setReason("");}}>회수</button>
                      )}
                      <button onClick={()=>{setSelectedCredential(item);setAction("REISSUE");setReason("");}}>재발급</button>
                    </>
                  ) : <b>처리 대기</b>}
                </span>
              </div>
            ))}
            {!items.length && <p className="case-empty">조건에 맞는 발급 기록이 없습니다.</p>}
          </div>
        </>
      )}

      {view==="DELIVERY" && (
        <>
          <div className="badge-ledger-toolbar">
            <input
              className="teacher-search"
              value={query}
              onChange={(e)=>setQuery(e.target.value)}
              placeholder="학생·배지·수령 이메일 검색"
            />
          </div>
          <div className="badge-delivery-list">
            {deliveryItems.map((item)=>(
              <article key={item.credentialId}>
                <div className="badge-delivery-main">
                  <img src={item.imageUri || ""} alt="" />
                  <span>
                    <strong>{item.displayName} · {item.credentialName}</strong>
                    <small>{item.destinationMasked || "수령 이메일 미등록"}</small>
                  </span>
                </div>
                <div className="badge-delivery-state">
                  <span>{deliveryStatus(item.emailStatus)}</span>
                  <strong>{claimStatus(item.claimStatus)}</strong>
                  <small>발송시도 {item.emailAttemptCount || 0}회</small>
                </div>
              </article>
            ))}
            {!deliveryItems.length && <p className="case-empty">수령상태 기록이 없습니다.</p>}
          </div>
        </>
      )}

      {view==="MANAGEMENT" && (
        <div className="badge-management-layout">
          <section className="badge-management-panel">
            <div className="panel-title">
              <div><strong>회수·재발급 요청</strong><span>교사가 요청하고 학교 관리자가 승인합니다.</span></div>
            </div>

            <div className="badge-management-request-list">
              {data.managementRequests.map((item)=>(
                <article key={item.requestId}>
                  <div>
                    <strong>{item.displayName} · {item.credentialName}</strong>
                    <span>{requestAction(item.action)} · {item.reason}</span>
                    <small>{formatDate(item.createdAt)} · {item.status==="REQUESTED" ? "승인 대기" : item.status}</small>
                  </div>
                  {item.status==="REQUESTED" && data.canManage && (
                    <div className="management-decision">
                      <input
                        value={decisionReason}
                        onChange={(e)=>setDecisionReason(e.target.value)}
                        placeholder="승인/반려 메모(선택)"
                      />
                      <button
                        className="primary"
                        disabled={busy!==""}
                        onClick={()=>decide(item,"APPROVE")}
                      >
                        승인
                      </button>
                      <button
                        className="secondary"
                        disabled={busy!==""}
                        onClick={()=>decide(item,"REJECT")}
                      >
                        반려
                      </button>
                    </div>
                  )}
                </article>
              ))}
              {!data.managementRequests.length && <p className="case-empty">회수·재발급 요청이 없습니다.</p>}
            </div>
          </section>

          <section className="badge-management-panel">
            <div className="panel-title">
              <div><strong>관리 대상 선택</strong><span>발급대장에서 선택하거나 여기서 검색하세요.</span></div>
            </div>
            <input
              className="teacher-search"
              value={query}
              onChange={(e)=>setQuery(e.target.value)}
              placeholder="학생·배지 검색"
            />
            <div className="badge-management-credential-list">
              {items.slice(0,20).map((item)=>(
                <button
                  key={item.credentialId}
                  className={selectedCredential?.credentialId===item.credentialId ? "active" : ""}
                  onClick={()=>setSelectedCredential(item)}
                >
                  <span><strong>{item.displayName}</strong><small>{item.credentialName} · {credentialStatus(item.status)}</small></span>
                </button>
              ))}
            </div>

            {selectedCredential && (
              <div className="badge-management-form">
                <strong>{selectedCredential.displayName} · {selectedCredential.credentialName}</strong>
                <div className="badge-action-choice">
                  {selectedCredential.status==="ACTIVE" && (
                    <button className={action==="REVOKE" ? "active" : ""} onClick={()=>setAction("REVOKE")}>회수</button>
                  )}
                  <button className={action==="REISSUE" ? "active" : ""} onClick={()=>setAction("REISSUE")}>재발급</button>
                </div>
                <textarea
                  value={reason}
                  onChange={(e)=>setReason(e.target.value)}
                  rows={3}
                  placeholder={action==="REVOKE" ? "회수 사유를 입력하세요." : "재발급 사유를 입력하세요."}
                />
                <button
                  className="primary"
                  disabled={busy!=="" || reason.trim().length<3}
                  onClick={submitRequest}
                >
                  {requestAction(action)} 요청 등록
                </button>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function Metric({label,value}:{label:string;value:number}){
  return <div className="badge-ledger-metric"><span>{label}</span><strong>{value}</strong></div>;
}
